// Vendor Program v2 PR 2 — the borrower's report review + sign-off surface,
// deep-linkable via ?report=<id> on the My goals tab. §1 narrative ("What
// {vendor} accomplished"), §2 goal grades, then the three-row star form.
// The whole point: a borrower signs off in UNDER A MINUTE — three taps and
// a button. Already-signed reports re-open read-only with a thank-you.

import { useEffect, useState } from "react";
import { Button, Textarea, useToast } from "@/design-system/components/ui";
import {
  formatReportMonth,
  portalBorrowerApi,
  type BorrowerVendorReportDto,
} from "../api/borrower";
import { BorrowerStarRating, BorrowerStarsDisplay } from "./BorrowerStarRating";
import { BorrowerCompleteChip, BorrowerScoreBar } from "./BorrowerGoalCard";

const RATING_ROWS = [
  { key: "serviceQuality", label: "Service quality" },
  { key: "responsiveness", label: "Responsiveness" },
  { key: "satisfaction", label: "Satisfaction" },
] as const;

type RatingKey = (typeof RATING_ROWS)[number]["key"];
type Ratings = Record<RatingKey, number | null>;

const EMPTY_RATINGS: Ratings = {
  serviceQuality: null,
  responsiveness: null,
  satisfaction: null,
};

export function BorrowerReportSignOff({
  reportId,
  onBack,
  onSignedOff,
}: {
  reportId: string;
  onBack: () => void;
  /** Lets the tab move the card out of its pending list immediately. */
  onSignedOff?: (updated: BorrowerVendorReportDto) => void;
}) {
  const [report, setReport] = useState<BorrowerVendorReportDto | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await portalBorrowerApi.getVendorReport(reportId);
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled)
          setLoadError(
            err instanceof Error ? err.message : "Couldn't load the report.",
          );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  if (loadError) {
    return (
      <div className="flex flex-col items-start gap-space-3">
        <p className="text-small text-status-error m-0" role="alert">
          {loadError}
        </p>
        <Button variant="secondary" size="sm" onClick={onBack}>
          Back to goals
        </Button>
      </div>
    );
  }
  if (!report) {
    return (
      <p className="text-small text-gray-mid" data-testid="report-loading">
        Loading the report…
      </p>
    );
  }

  const month = formatReportMonth(report.periodStart);

  return (
    <section
      aria-label={`${report.vendorName} report for ${month}`}
      className="flex flex-col gap-space-4"
      data-testid="report-sign-off"
    >
      <button
        type="button"
        className="self-start text-small text-gold-shadow hover:underline"
        onClick={onBack}
        data-testid="report-back"
      >
        ← Back to goals
      </button>

      <div className="rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3">
        <div>
          <h2 className="font-display font-bold text-h2 m-0">
            What {report.vendorName} accomplished in {month}
          </h2>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            {report.loanLabel} · {report.roleOnLoan}
          </p>
        </div>
        {report.publicNarrative ? (
          <p
            className="text-small text-onyx m-0 whitespace-pre-wrap"
            data-testid="report-narrative"
          >
            {report.publicNarrative}
          </p>
        ) : (
          <p className="text-small text-gray-mid m-0 italic">
            No summary was written for this month.
          </p>
        )}
      </div>

      {report.goalGrades.length > 0 && (
        <div className="rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3">
          <h3 className="font-display font-semibold text-h4 m-0">
            How your goals progressed
          </h3>
          <ul className="m-0 p-0 list-none flex flex-col gap-space-3">
            {report.goalGrades.map((grade) => (
              <li
                key={grade.goalId}
                className="flex flex-col gap-space-1"
                data-testid={`report-grade-${grade.goalId}`}
              >
                <div className="flex items-center gap-space-2 flex-wrap">
                  <span className="text-small font-medium text-onyx">
                    {grade.goalTitle}
                  </span>
                  {grade.isComplete && <BorrowerCompleteChip />}
                </div>
                <BorrowerScoreBar
                  score={grade.score}
                  label={`${grade.goalTitle}: ${grade.score} out of 100`}
                />
                {grade.note && (
                  <p className="text-caption text-gray-mid m-0 italic">
                    {grade.note}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.signedOff && report.signOff ? (
        <SignedOffSummary report={report} />
      ) : (
        <SignOffForm
          report={report}
          onSignedOff={(updated) => {
            setReport(updated);
            onSignedOff?.(updated);
          }}
        />
      )}
    </section>
  );
}

function SignedOffSummary({ report }: { report: BorrowerVendorReportDto }) {
  const signOff = report.signOff!;
  return (
    <div
      className="rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3"
      data-testid="sign-off-summary"
    >
      <h3 className="font-display font-semibold text-h4 m-0">Your ratings</h3>
      <p className="text-small text-status-success m-0" role="status">
        Thank you — your ratings are in. They help {report.vendorName} and
        Phoenix keep your progress on track.
      </p>
      <dl className="m-0 flex flex-col gap-space-2">
        {RATING_ROWS.map((row) => (
          <div key={row.key} className="flex items-center gap-space-3">
            <dt className="text-small text-gray-mid w-40 shrink-0">
              {row.label}
            </dt>
            <dd className="m-0">
              <BorrowerStarsDisplay
                value={signOff[row.key]}
                label={`${row.label}: ${signOff[row.key]} of 4 stars`}
                testId={`signed-${row.key}`}
              />
            </dd>
          </div>
        ))}
      </dl>
      {signOff.feedback && (
        <p className="text-small text-onyx m-0 whitespace-pre-wrap">
          “{signOff.feedback}”
        </p>
      )}
    </div>
  );
}

function SignOffForm({
  report,
  onSignedOff,
}: {
  report: BorrowerVendorReportDto;
  onSignedOff: (updated: BorrowerVendorReportDto) => void;
}) {
  const toast = useToast();
  const [ratings, setRatings] = useState<Ratings>(EMPTY_RATINGS);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const allRated = RATING_ROWS.every((row) => ratings[row.key] !== null);

  const submit = async () => {
    if (!allRated || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const trimmed = feedback.trim();
      const updated = await portalBorrowerApi.signOffVendorReport(
        report.reportId,
        {
          serviceQuality: ratings.serviceQuality!,
          responsiveness: ratings.responsiveness!,
          satisfaction: ratings.satisfaction!,
          ...(trimmed ? { feedback: trimmed } : {}),
        },
      );
      toast.success("Ratings sent — thank you!");
      onSignedOff(updated);
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Couldn't send your ratings. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-gold bg-white-pure p-space-4 flex flex-col gap-space-3"
      data-testid="sign-off-form"
    >
      <div>
        <h3 className="font-display font-semibold text-h4 m-0">
          How did {report.vendorName} do this month?
        </h3>
        <p className="text-caption text-gray-mid m-0 mt-space-1">
          Three quick ratings — it takes under a minute.
        </p>
      </div>

      <div className="flex flex-col gap-space-2">
        {RATING_ROWS.map((row) => (
          <div key={row.key} className="flex items-center gap-space-3 flex-wrap">
            <span className="text-small text-onyx w-40 shrink-0">
              {row.label}
            </span>
            <BorrowerStarRating
              label={row.label}
              value={ratings[row.key]}
              disabled={submitting}
              onChange={(value) =>
                setRatings((prev) => ({ ...prev, [row.key]: value }))
              }
              testId={`rating-${row.key}`}
            />
          </div>
        ))}
      </div>

      <label className="flex flex-col gap-space-1">
        <span className="text-small text-gray-mid">
          Anything you&apos;d like to add? (optional)
        </span>
        <Textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={submitting}
          rows={3}
          placeholder="A note for Phoenix about how things are going…"
          data-testid="sign-off-feedback"
        />
      </label>

      {submitError && (
        <p className="text-small text-status-error m-0" role="alert">
          {submitError}
        </p>
      )}

      <div className="flex items-center gap-space-2">
        <Button
          variant="primary"
          onClick={() => void submit()}
          disabled={!allRated || submitting}
          data-testid="sign-off-submit"
        >
          {submitting ? "Sending…" : "Send my ratings"}
        </Button>
        {!allRated && (
          <span className="text-caption text-gray-mid">
            Rate all three areas to send.
          </span>
        )}
      </div>
    </div>
  );
}
